import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  TrendingUp,
  TrendingDown,
  Assessment,
} from '@mui/icons-material';
import * as api from '../services/api';

const CapacityPlanningV2DettaglioPage: React.FC = () => {
  const { anno, mese } = useParams<{ anno: string; mese: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<api.CapacityPlanningV2DettaglioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mesiNomi = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  // Recupera strutture dai query params
  const strutture = new URLSearchParams(location.search).get('strutture')?.split(',').filter(s => s) || undefined;

  useEffect(() => {
    if (anno && mese) {
      loadData();
    }
  }, [anno, mese]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.getCapacityPlanningV2Dettaglio(
        parseInt(anno!),
        parseInt(mese!),
        strutture
      );
      setData(result);
    } catch (err: any) {
      console.error('Errore caricamento dettaglio:', err);
      setError(err.response?.data?.error || 'Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/reportistica/capacity-planning-v2')}
          sx={{ mt: 2 }}
        >
          Torna alla Dashboard
        </Button>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const meseNome = mesiNomi[data.mese - 1];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/reportistica/capacity-planning-v2')}
          sx={{ mr: 2 }}
        >
          Indietro
        </Button>
        <Typography variant="h4" fontWeight="bold">
          Dettaglio {meseNome} {data.anno}
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Assessment sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Demand Totale
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                {data.summary.total_demand} gg
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {data.summary.num_progetti} progetti attivi
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Assessment sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Supply Totale
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                {data.summary.total_supply} gg
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Allocazioni pianificate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{
            background: data.summary.total_gap >= 0
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {data.summary.total_gap >= 0
                  ? <TrendingUp sx={{ color: 'white', mr: 1 }} />
                  : <TrendingDown sx={{ color: 'white', mr: 1 }} />
                }
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Gap Totale
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                {data.summary.total_gap >= 0 ? '+' : ''}{data.summary.total_gap} gg
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {data.summary.total_gap < 0 ? 'Sottodimensionato' : 'Capacità residua'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabella Progetti */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Dettaglio per Progetto
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Confronto tra demand mensile teorico e supply effettivo da allocazioni
        </Typography>

        {data.progetti.length === 0 ? (
          <Alert severity="info">Nessun progetto attivo in questo mese</Alert>
        ) : (
          data.progetti.map((progetto) => (
            <Accordion key={progetto.progetto_id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                  <Typography sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                    {progetto.progetto_titolo}
                  </Typography>
                  <Chip
                    label={`Demand: ${progetto.demand_mensile} gg`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                  <Chip
                    label={`Supply: ${progetto.supply_mensile} gg`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                  <Chip
                    label={`Gap: ${progetto.gap >= 0 ? '+' : ''}${progetto.gap} gg`}
                    size="small"
                    color={progetto.gap >= 0 ? 'success' : 'error'}
                    variant="filled"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 2, pr: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Budget totale: {progetto.budget_totale} gg • Durata: {progetto.durata_mesi} mesi •
                    Budget mensile: {progetto.demand_mensile} gg/mese
                  </Typography>

                  {progetto.allocazioni.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      ⚠️ Nessuna allocazione pianificata per questo progetto nel mese
                    </Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Risorsa</strong></TableCell>
                            <TableCell><strong>Struttura</strong></TableCell>
                            <TableCell align="right"><strong>Percentuale</strong></TableCell>
                            <TableCell align="right"><strong>Giorni</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {progetto.allocazioni.map((alloc, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                {alloc.risorsa_cognome} {alloc.risorsa_nome}
                              </TableCell>
                              <TableCell>{alloc.risorsa_struttura}</TableCell>
                              <TableCell align="right">{alloc.percentuale}%</TableCell>
                              <TableCell align="right">{alloc.giorni} gg</TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell colSpan={3}><strong>Totale Supply</strong></TableCell>
                            <TableCell align="right">
                              <strong>{progetto.supply_mensile} gg</strong>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default CapacityPlanningV2DettaglioPage;
